const RECENT_ROUTE_STORAGE_KEY = 'planner.recentRoute.v1';
const RECENT_ROUTE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 90;
const RECENT_ROUTE_PATH_PATTERN = /^\/(?:(?:en|it|es|fr|de)\/)?now\/[^?#\s]+(?:\?[^#\s]*)?$/;

export interface RecentRoute {
  routeId: string;
  path: string;
  visitedAt: string;
}

export interface RecentRouteIds {
  speciesId: string;
  techniqueId: string;
  rigId: string | null;
  locationTypeId: string | null;
}

const asBoundedString = (value: unknown, maxLength: number): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed && trimmed.length <= maxLength ? trimmed : null;
};

export const parseRecentRouteIds = (routeId: string): RecentRouteIds | null => {
  const [speciesId, techniqueId, rigId = 'none', locationTypeId = 'none', ...rest] = routeId.split(':');
  if (!speciesId || !techniqueId || rest.length > 0) {
    return null;
  }

  return {
    speciesId,
    techniqueId,
    rigId: rigId === 'none' ? null : rigId,
    locationTypeId: locationTypeId === 'none' ? null : locationTypeId
  };
};

export const parseStoredRecentRoute = (raw: string | null, now = Date.now()): RecentRoute | null => {
  if (!raw) {
    return null;
  }

  try {
    const candidate = JSON.parse(raw) as Record<string, unknown>;
    const routeId = asBoundedString(candidate.routeId, 300);
    const path = asBoundedString(candidate.path, 800);
    const visitedAt = asBoundedString(candidate.visitedAt, 64);
    const visitedAtMs = visitedAt ? Date.parse(visitedAt) : Number.NaN;

    if (
      !routeId ||
      !parseRecentRouteIds(routeId) ||
      !path ||
      !RECENT_ROUTE_PATH_PATTERN.test(path) ||
      !visitedAt ||
      !Number.isFinite(visitedAtMs) ||
      visitedAtMs > now + 1000 * 60 * 5 ||
      now - visitedAtMs > RECENT_ROUTE_MAX_AGE_MS
    ) {
      return null;
    }

    return { routeId, path, visitedAt };
  } catch {
    return null;
  }
};

export const getRecentRoute = (): RecentRoute | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const recentRoute = parseStoredRecentRoute(window.localStorage.getItem(RECENT_ROUTE_STORAGE_KEY));
  if (!recentRoute) {
    window.localStorage.removeItem(RECENT_ROUTE_STORAGE_KEY);
  }
  return recentRoute;
};

export const storeRecentRoute = (route: Omit<RecentRoute, 'visitedAt'> & { visitedAt?: string }): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const candidate: RecentRoute = {
    routeId: route.routeId,
    path: route.path,
    visitedAt: route.visitedAt || new Date().toISOString()
  };
  const normalized = parseStoredRecentRoute(JSON.stringify(candidate));

  if (normalized) {
    window.localStorage.setItem(RECENT_ROUTE_STORAGE_KEY, JSON.stringify(normalized));
  }
};

export const clearRecentRoute = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(RECENT_ROUTE_STORAGE_KEY);
};

export const RECENT_ROUTE_TEST_CONSTANTS = {
  storageKey: RECENT_ROUTE_STORAGE_KEY,
  maxAgeMs: RECENT_ROUTE_MAX_AGE_MS
} as const;
